/* eslint-disable react/prop-types */
import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import { FormControl, FormLabel, Grid, TextField, InputAdornment } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Box, Stack, alpha, useTheme, IconButton, Divider } from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Description as DescriptionIcon,
  Title as TitleIcon,
  Update as UpdateIcon
} from '@mui/icons-material';

const EditPackages = (props) => {
  const { open, handleClose, editPackagesData, editData } = props;
  const theme = useTheme();

  // -----------  validationSchema
  const validationSchema = yup.object({
    title: yup.string().required('El título es requerido'),
    amount: yup.number().required('El monto es requerido').min(0, 'El monto no puede ser negativo'),
    description: yup.string().required('La descripción es requerida').min(10, 'La descripción debe tener al menos 10 caracteres'),
    days: yup
      .string()
      .required('Los días son requeridos')
      .test('is-valid-range', 'Los días deben estar entre 1 y 365', (value) => {
        const num = parseInt(value);
        return num >= 1 && num <= 365;
      }),
    freeDays: yup.string().test('is-valid-free-days', 'Los días gratis deben estar entre 0 y 365', (value) => {
      if (value === '' || value === undefined || value === null) return true;
      const num = parseInt(value);
      return num >= 0 && num <= 365;
    })
  });

  // -----------   initialValues
  const initialValues = {
    title: editData?.title || '',
    amount: editData?.amount || '',
    description: editData?.description || '',
    days: editData?.days || '',
    freeDays: editData?.freeDays ?? 0
  };

  // formik
  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const packagesPayload = {
        title: values.title,
        days: values.days,
        freeDays: Number(values.freeDays) || 0,
        amount: values.amount,
        description: values.description
      };
      editPackagesData(packagesPayload);
      handleClose();
      formik.resetForm();
    }
  });

  const handleCloseModal = () => {
    formik.resetForm();
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: theme.shadows[12]
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
            color: 'white',
            p: 3
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Editar Paquete
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Modifica los detalles del plan de suscripción
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 3 }}>
        <form>
          <Stack spacing={3}>
            {/* Basic Information Header */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <EditIcon sx={{ color: theme.palette.warning.main }} />
                <Typography variant="h6" fontWeight={600} color="warning">
                  Información del Paquete
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Actualiza los detalles del paquete de suscripción
              </Typography>
            </Box>

            <Divider />

            {/* Form Fields */}
            <Grid container spacing={3}>
              {/* Title */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TitleIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                      <Typography variant="body2" fontWeight={600}>
                        Título del Paquete
                      </Typography>
                    </Stack>
                  </FormLabel>
                  <TextField
                    id="title"
                    name="title"
                    placeholder="Nombre del paquete"
                    fullWidth
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    error={formik.touched.title && Boolean(formik.errors.title)}
                    helperText={formik.touched.title && formik.errors.title}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </FormControl>
              </Grid>

              {/* Amount */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <MoneyIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                      <Typography variant="body2" fontWeight={600}>
                        Precio (USD)
                      </Typography>
                    </Stack>
                  </FormLabel>
                  <TextField
                    id="amount"
                    name="amount"
                    type="number"
                    placeholder="0.00"
                    fullWidth
                    value={formik.values.amount}
                    onChange={formik.handleChange}
                    error={formik.touched.amount && Boolean(formik.errors.amount)}
                    helperText={formik.touched.amount && formik.errors.amount}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MoneyIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </FormControl>
              </Grid>

              {/* Days */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ScheduleIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
                      <Typography variant="body2" fontWeight={600}>
                        Duración (días)
                      </Typography>
                    </Stack>
                  </FormLabel>
                  <TextField
                    id="days"
                    name="days"
                    type="number"
                    fullWidth
                    placeholder="Días de duración"
                    value={formik.values.days}
                    onChange={formik.handleChange}
                    error={formik.touched.days && Boolean(formik.errors.days)}
                    helperText={(formik.touched.days && formik.errors.days) || 'Entre 1 y 365 días'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ScheduleIcon sx={{ fontSize: 20, color: theme.palette.info.main }} />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </FormControl>
              </Grid>

              {/* Free Days */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ScheduleIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                      <Typography variant="body2" fontWeight={600}>
                        Días Gratis (prueba)
                      </Typography>
                    </Stack>
                  </FormLabel>
                  <TextField
                    id="freeDays"
                    name="freeDays"
                    type="number"
                    fullWidth
                    placeholder="0"
                    value={formik.values.freeDays}
                    onChange={formik.handleChange}
                    error={formik.touched.freeDays && Boolean(formik.errors.freeDays)}
                    helperText={(formik.touched.freeDays && formik.errors.freeDays) || 'El plan comienza a cobrar después de estos días'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ScheduleIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </FormControl>
              </Grid>

              {/* Preview */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: alpha(theme.palette.warning.main, 0.05),
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Vista Previa Actualizada
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formik.values.title || 'Nombre del paquete'} - ${formik.values.amount || '0'} por {formik.values.days || '0'} días
                    {Number(formik.values.freeDays) > 0 ? ` (+ ${formik.values.freeDays} días gratis de prueba)` : ''}
                  </Typography>
                </Box>
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <DescriptionIcon sx={{ fontSize: 18, color: theme.palette.secondary.main }} />
                      <Typography variant="body2" fontWeight={600}>
                        Descripción del Paquete
                      </Typography>
                    </Stack>
                  </FormLabel>
                  <TextField
                    id="description"
                    name="description"
                    multiline
                    rows={4}
                    placeholder="Describe las características y beneficios incluidos..."
                    fullWidth
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    error={formik.touched.description && Boolean(formik.errors.description)}
                    helperText={(formik.touched.description && formik.errors.description) || 'Mínimo 10 caracteres'}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </FormControl>
              </Grid>
            </Grid>

            {/* Information Box */}
            <Box
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.warning.main, 0.05),
                borderRadius: 2,
                borderLeft: `4px solid ${theme.palette.warning.main}`
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>Nota:</strong> Los cambios realizados se aplicarán inmediatamente. Los hoteles ya suscritos a este paquete
                mantendrán sus términos actuales hasta la renovación.
              </Typography>
            </Box>
          </Stack>
        </form>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={handleCloseModal}
          variant="outlined"
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            px: 3
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          startIcon={<UpdateIcon />}
          color="warning"
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            px: 3
          }}
        >
          Guardar Cambios
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPackages;
